<template>
	<form @submit.prevent="save" ref="form" autocomplete="off">
		<b-loading :active="isLoading"></b-loading>
		<div class="modal-card">
			<header class="modal-card-head">
				<p class="modal-card-title">Szczegóły użytkownika: {{ currentUser.username }}</p>
				<button
					type="button"
					class="delete"
					aria-label="close"
					@click="$parent.$emit('close')"
				></button>
			</header>
			<section class="modal-card-body">
				<div class="columns">
					<div class="column">
						<div class="info" v-if="isGmailActivated">
							<p>Wysyłka za pomocą Gmail jest aktywna.</p>
							<p>Możesz już wysyłać powiadomienia do klientów z tego konta.</p>
						</div>
						<div class="info" v-if="isGmailDeactivated">
							<p>Wysyłka za pomocą Gmail NIE jest już aktywna.</p>
							<p>Nie możesz już wysyłać powiadomień do klientów z tego konta.</p>
						</div>
						<h2>{{ isEditModeEnabled ? 'Edycja' : 'Szczegóły' }} użytkownika</h2>
						<div class="group">
							<div class="field is-horizontal field--username">
								<div class="field-label">Nazwa użytkownika:</div>
								<editable
									:isEditMode="isEditModeEnabled"
									:value="currentUser.username"
									required
									placeholder="Nazwa użytkownika"
									type="text"
									autocomplete="off"
									@input="currentUser.username = $event"
								/>
							</div>
							<div class="field is-horizontal field--plainPassword" v-if="isEditModeEnabled">
								<div class="field-label">
									Nowe hasło:
									<small>(pozostaw puste aby nie zmieniać)</small>
								</div>
								<editable
									:isEditMode="isEditModeEnabled"
									:value="currentUser.plainPassword"
									placeholder="●●●●●●●"
									type="password"
									autocomplete="off"
									@input="currentUser.plainPassword = $event"
								/>
							</div>
							<hr />
							<div class="field is-horizontal field--isAdmin">
								<div class="field-label">Administrator:</div>
								<editable
									:isEditMode="isEditModeEnabled"
									:checked="currentUser.isAdmin"
									type="switch"
									@input="currentUser.isAdmin = $event"
								/>
							</div>
							<div class="field is-horizontal field--isActive" v-if="isAdmin">
								<div class="field-label">Konto aktywne:</div>
								<editable
									:isEditMode="isEditModeEnabled"
									:checked="currentUser.isActive"
									type="switch"
									@input="currentUser.isActive = $event"
								/>
							</div>
						</div>
						<div v-if="canSendAsUser">
							<h2>Wysyłka wiadomości email</h2>
							<div class="group">
								<button
									class="button is-small mailer-info-button"
									@click.prevent="isMailerInfoVisible = !isMailerInfoVisible"
								>
									{{ isMailerInfoVisible ? 'Ukryj' : 'Pokaż' }} wyjaśnienie
								</button>
								<div class="info info--email" v-if="isMailerInfoVisible">
									<p>
										Włączenie tej opcji pozwoli na wysyłanie wiadomości email do klienta o montażu z
										konta email użytkownika.
									</p>
									<p>
										Aby opcja była aktywna należy poprawnie wypełnić poniższe pola z danymi konta
										email oraz kliknąć przycisk <b>"Wyślij wiadomość"</b> w celu przetestowania
										wysyłki.
									</p>
									<p>
										Jeśli wiadomość testowa zostanie poprawnie wysłana, opcja zostanie aktywowana i
										będzie można użyć tego konta email do wysyłania wiadomości.
									</p>
								</div>
								<div class="field is-horizontal field--mailerIsEnabled" v-if="isAdmin">
									<div class="field-label">Wysyłka włączona:</div>
									<editable
										:isEditMode="isEditModeEnabled"
										:checked="currentUser.mailerIsEnabled"
										type="switch"
										@input="
											currentUser.mailerIsEnabled = $event
											currentUser.mailerMode = $event
												? currentUser.mailerMode || 'regular'
												: undefined
										"
									/>
								</div>
								<div class="field is-horizontal field--mailerIsActive" v-if="isAdmin">
									<div class="field-label">Wysyłka aktywowana:</div>
									<editable
										:isEditMode="false"
										:checked="currentUser.mailerIsActive"
										type="switch"
										@input="currentUser.mailerIsActive = $event"
									/>
								</div>
								<div v-if="currentUser.mailerIsEnabled" class="field is-horizontal">
									<div class="field-label">Skrzynka email:</div>
									<b-field horizontal v-if="isEditModeEnabled" class="field field-body is-grouped">
										<b-field>
											<b-radio-button
												:value="currentUser.mailerMode"
												native-value="regular"
												@input="currentUser.mailerMode = 'regular'"
											>
												<span>Zwykła skrzynka</span>
											</b-radio-button>
											<b-radio-button
												:value="currentUser.mailerMode"
												native-value="gmail"
												@input="currentUser.mailerMode = 'gmail'"
												:disabled="!canSendWithGmail"
											>
												<span>Skrzynka Gmail</span>
											</b-radio-button>
										</b-field>
									</b-field>
									<b-field v-if="!isEditModeEnabled" class="field field-body is-grouped">
										<span>
											{{
												currentUser.mailerMode === 'gmail' ? 'Skrzynka Gmail' : 'Zwykła skrzynka'
											}}
										</span>
									</b-field>
								</div>
								<div class="field is-horizontal" v-if="currentUser.mailerIsEnabled">
									<div class="field-label">Ustawienia skrzynki:</div>
									<b-field
										horizontal
										class="field field-body is-grouped"
										v-if="currentUser.mailerMode === 'gmail'"
									>
										<a
											class="button button--save"
											:href="urlToSignInWithGoogle"
											v-if="!currentUser.googleEmail || !currentUser.googleRefreshToken"
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												xmlns:xlink="http://www.w3.org/1999/xlink"
												width="20px"
												height="20px"
												viewBox="-0.5 0 48 48"
												version="1.1"
												style="margin-right: 5px"
											>
												<g
													id="Icons"
													stroke="none"
													stroke-width="1"
													fill="none"
													fill-rule="evenodd"
												>
													<g id="Color-" transform="translate(-401.000000, -860.000000)">
														<g id="Google" transform="translate(401.000000, 860.000000)">
															<path
																d="M9.82727273,24 C9.82727273,22.4757333 10.0804318,21.0144 10.5322727,19.6437333 L2.62345455,13.6042667 C1.08206818,16.7338667 0.213636364,20.2602667 0.213636364,24 C0.213636364,27.7365333 1.081,31.2608 2.62025,34.3882667 L10.5247955,28.3370667 C10.0772273,26.9728 9.82727273,25.5168 9.82727273,24"
																id="Fill-1"
																fill="#FBBC05"
															></path>
															<path
																d="M23.7136364,10.1333333 C27.025,10.1333333 30.0159091,11.3066667 32.3659091,13.2266667 L39.2022727,6.4 C35.0363636,2.77333333 29.6954545,0.533333333 23.7136364,0.533333333 C14.4268636,0.533333333 6.44540909,5.84426667 2.62345455,13.6042667 L10.5322727,19.6437333 C12.3545909,14.112 17.5491591,10.1333333 23.7136364,10.1333333"
																id="Fill-2"
																fill="#EB4335"
															></path>
															<path
																d="M23.7136364,37.8666667 C17.5491591,37.8666667 12.3545909,33.888 10.5322727,28.3562667 L2.62345455,34.3946667 C6.44540909,42.1557333 14.4268636,47.4666667 23.7136364,47.4666667 C29.4455,47.4666667 34.9177955,45.4314667 39.0249545,41.6181333 L31.5177727,35.8144 C29.3995682,37.1488 26.7323182,37.8666667 23.7136364,37.8666667"
																id="Fill-3"
																fill="#34A853"
															></path>
															<path
																d="M46.1454545,24 C46.1454545,22.6133333 45.9318182,21.12 45.6113636,19.7333333 L23.7136364,19.7333333 L23.7136364,28.8 L36.3181818,28.8 C35.6879545,31.8912 33.9724545,34.2677333 31.5177727,35.8144 L39.0249545,41.6181333 C43.3393409,37.6138667 46.1454545,31.6490667 46.1454545,24"
																id="Fill-4"
																fill="#4285F4"
															></path>
														</g>
													</g>
												</g>
											</svg>
											<span>Zaloguj się z Google</span></a
										>
										<div v-if="currentUser.googleEmail && currentUser.googleRefreshToken">
											<span>{{ currentUser.googleName }}</span>

											<a
												class="button is-danger is-small"
												style="margin-left: 10px"
												:href="urlToSignOutFromGoogle"
												><img
													v-if="false"
													:src="currentUser.googlePicture"
													style="
														width: 20px;
														height: 20px;
														border-radius: 99999px;
														margin-right: 10px;
													"
												/>
												<span>Wyloguj się z Google</span></a
											>
										</div>
									</b-field>
									<b-field
										class="field field-body is-grouped"
										v-if="currentUser.mailerMode === 'regular'"
									>
										<div v-if="!isEditModeEnabled">
											<p>
												Host: <i>{{ currentUser.mailerHost || '-' }}</i>
											</p>
											<p>
												Email: <i>{{ currentUser.mailerEmail || '-' }}</i>
											</p>
											<p>Hasło: <i>ukryte</i></p>
										</div>
										<div style="margin-bottom: 0.75rem">
											<div
												class="field is-horizontal field--mailerEmail"
												v-if="isAdmin && isEditModeEnabled"
											>
												<div class="field-label">Email:</div>
												<editable
													:isEditMode="isEditModeEnabled"
													:value="currentUser.mailerEmail"
													placeholder="jan.kowalski@example.com"
													type="email"
													autocomplete="off"
													@input="currentUser.mailerEmail = $event"
												/>
											</div>
										</div>
										<div style="margin-bottom: 0.75rem">
											<div
												class="field is-horizontal field--mailerPassword"
												v-if="isAdmin && currentUser.mailerIsEnabled && isEditModeEnabled"
											>
												<div class="field-label">Hasło:</div>
												<editable
													:isEditMode="isEditModeEnabled"
													:value="currentUser.mailerPassword"
													placeholder="*******"
													type="password"
													autocomplete="off"
													@input="currentUser.mailerPassword = $event"
												/>
											</div>
										</div>
										<div style="margin-bottom: 0.75rem">
											<div
												class="field is-horizontal field--mailerHost"
												v-if="isAdmin && currentUser.mailerIsEnabled && isEditModeEnabled"
											>
												<div class="field-label">Host:</div>
												<editable
													:isEditMode="isEditModeEnabled"
													:value="currentUser.mailerHost"
													placeholder="mailer@example.com"
													type="text"
													autocomplete="off"
													@input="currentUser.mailerHost = $event"
												/>
											</div>
										</div>
									</b-field>
								</div>
								<div
									class="field is-horizontal field--mailerIsActive"
									v-if="isAdmin && currentUser.mailerIsEnabled"
									:disabled="isSending"
								>
									<div class="field-label">Test wysyłania:</div>
									<div class="field-body" style="flex-direction: column">
										<button
											class="button is-info button--save"
											:disabled="isSending"
											@click.prevent="handleTestMailer"
											:class="{ 'is-loading': isSending }"
											style="align-self: flex-start"
										>
											<i class="mdi">send</i> Wyślij wiadomość
										</button>
										<div v-if="isMailSent" class="mail-sent-info">
											<p>Wiadomość wysłana pomyślnie.</p>
											<p v-if="currentUser.mailerMode === 'regular'">Proszę zapisać zmiany.</p>
											<p>
												Ten użytkownik musi się przelogować, aby mógł wysyłać wiadomości ze swojej
												skrzynki.
											</p>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="column" v-if="!isEditModeEnabled">
						<h2>Dodane zamówienia</h2>
						<div class="group orders">
							<ul v-if="currentUser.orders && currentUser.orders.length">
								<li v-for="order in currentUser.orders" :key="order.id">
									#{{ order.id }} -
									<a href="" @click.prevent="$parent.$emit('order-click', order)" class="link">
										{{ order.clientName }}
									</a>
									<small class="created-date"
										>dodano {{ formatDate(order.createdDate, 'HH:mm DD/MM/YYYY') }}</small
									>
								</li>
							</ul>
							<p v-else>Brak zamówień.</p>
						</div>
					</div>
				</div>
			</section>
			<footer class="modal-card-foot">
				<div class="level">
					<div class="level-left">
						<button type="submit" class="button is-success button--save" v-if="isEditModeEnabled">
							<i class="mdi">save</i> Zapisz zmiany
						</button>
						<button
							class="button is-primary button--edit"
							@click.prevent="isEditModeEnabled = true"
							v-else
						>
							<i class="mdi">edit</i> Edytuj
						</button>
						<button class="button is-white button--cancel" @click.prevent="$parent.$emit('close')">
							Zamknij
						</button>
					</div>
					<div class="level-right">
						<button
							class="button is-danger is-small button--delete"
							@click.prevent="showDeleteDialog"
						>
							Usuń
						</button>
					</div>
				</div>
			</footer>
		</div>
	</form>
</template>

<script>
import moment from 'moment'
import { mapState, mapGetters } from 'vuex'

import bus from '@/EventBus'
import config from '@/config'
import EditableField from '@/components/EditableField'

export default {
	name: 'ViewUserModal',
	components: {
		editable: EditableField
	},
	props: {
		isGmailActivated: {
			type: Boolean,
			required: true
		},
		isGmailDeactivated: {
			type: Boolean,
			required: true
		}
	},
	data() {
		return {
			isLoading: false,
			isSending: false,
			isEditModeEnabled: false,
			// isMailerEnabled: false,
			isMailerInfoVisible: false,
			isMailSent: false,
			canSendAsUser: config.company.canSendAsUser
		}
	},
	computed: {
		...mapState('users', ['currentUser']),
		...mapGetters('auth', ['isAdmin', 'user']),
		urlToSignInWithGoogle() {
			return `${config.service.apiRootURL}sign-in-with-google?userId=${this.currentUser.id}`
		},
		urlToSignOutFromGoogle() {
			return `${config.service.apiRootURL}sign-out-from-google?userId=${this.currentUser.id}`
		},
		canSendWithGmail() {
			return config.company.canSendWithGmail
		}
	},
	methods: {
		formatDate(value, format) {
			return moment(value).format(format)
		},
		save() {
			this.isLoading = true
			// if (this.isMailerEnabled) {
			// 	this.currentUser.mailerIsEnabled = true
			// }
			this.$store.dispatch('users/updateUser').then((user) => {
				this.isLoading = false
				this.isEditModeEnabled = false
				this.$snackbar.open(`Użytkownik #${user.username} został edytowany.`)
				bus.$emit('user-updated')
			})
		},
		showDeleteDialog() {
			this.$dialog.confirm({
				title: `Usuwanie użytkownika`,
				message: `Czy na pewno usunąć użytkownika "${this.currentUser.username}" wraz ze wszystkimi dodanymi przez niego zamówieniami?`,
				confirmText: 'Tak, usuń',
				cancelText: 'Anuluj',
				type: 'is-danger',
				hasIcon: true,
				onConfirm: () => this.destroy()
			})
		},
		destroy() {
			this.isLoading = true
			this.$store.dispatch('users/deleteUser', this.currentUser).then((user) => {
				this.isLoading = false
				this.$snackbar.open(`Użytkownik ${user.username} został usunięty.`)
				this.$parent.$emit('close')
				bus.$emit('user-deleted')
			})
		},
		handleTestMailer() {
			this.isSending = true
			this.$store
				.dispatch('orders/sendEmail', {
					userId: this.currentUser.id,
					method: 'user',
					mailer: {
						email: this.currentUser.mailerEmail,
						password: this.currentUser.mailerPassword,
						host: this.currentUser.mailerHost,
						mailerMode: this.currentUser.mailerMode,
						googleEmail: this.currentUser.googleEmail,
						googleRefreshToken: this.currentUser.googleRefreshToken
					}
				})
				.then(({ data }) => {
					const userToSet = { ...this.currentUser }
					userToSet.mailerIsActive = true
					return this.$store.dispatch('users/setCurrentUser', userToSet).then(() => {
						this.$snackbar.open(`Testowy email został wysłany do ${data.recipient}`)
						this.currentUser.mailerIsActive = true
						this.isSending = false
						this.isMailSent = true
					})
				})
				.catch(() => {
					this.isSending = false
					this.currentUser.mailerIsActive = false
					this.$snackbar.open(`Wystąpił błąd podczas wysyłania wiadomości email.`)
				})
		}
	},
	created() {
		// this.isMailerEnabled = this.currentUser.mailerIsEnabled || false
	},
	destroyed() {
		this.$store.dispatch('users/setCurrentUser', null)
	}
}
</script>

<style lang="scss" scoped>
.modal-card-body {
	padding-bottom: 50px;
}
.orders {
	padding: 25px;
	ul {
		list-style: circle;
		margin-left: 10px;
	}
	li {
		padding-bottom: 10px;
	}
}
.created-date {
	display: block;
	color: #aaa;
}
.info {
	margin-bottom: 20px;
	padding: 10px;
	font-size: 12px;
	border: 1px solid #c7e0e7;
	background-color: #edf7ff;
	border-radius: 3px;
	&--email {
		margin: 0 20px 20px 20px;
	}
}
.mailer-info-button {
	margin: 20px 0 20px 20px;
}
.mail-sent-info {
	color: green;
	padding: 10px;
	margin-top: 10px;
	border: 1px solid green;
	background-color: #f0fff0;
	border-radius: 3px;
}
.send-button-wrapper {
	display: flex;
	align-items: center;
}
</style>
